import { matchesSearch } from '@/features/erp/ui/calm-logic'
import { ADMIN_ROLES, PERMISSIONS, ROLE_LABELS, can, type AdminRole, type Permission } from '../shared/permissions'
import type { LogEntry, LogKind, Person } from './contract'

/* Spec COG-009 — Equipe e permissões: pure helpers of the screen (filters, countdown, the role matrix built from
   shared/permissions.ts, so the screen never has its own copy of the rules). */

export const PERMISSION_LABELS: Record<Permission, string> = {
    'fichas.ver': 'Ver fichas digitais',
    'fichas.editar': 'Corrigir fichas',
    'export.basico': 'Exportar sem dados pessoais',
    'export.dados_pessoais': 'Exportar com dados pessoais (pede motivo)',
    'recorrencias.ver': 'Ver recorrências e pagamentos',
    'estorno.pedir': 'Pedir estorno ou cancelamento',
    'estorno.aprovar': 'Aprovar estorno ou cancelamento (nunca o próprio pedido)',
    'estorno.aprovar_alto': 'Segunda aprovação acima de R$ 500',
    retencao: 'Cancelamento, retenção e pré-ativos',
    conciliacao: 'Fazer a conciliação Braspag × CRM',
    'conciliacao.revisar': 'Revisar a conciliação (nunca a própria)',
    moderacao: 'Moderar mensagens',
    'salvaguarda.relatar': 'Relatar preocupação de salvaguarda (todos)',
    'salvaguarda.casos': 'Ver casos de salvaguarda (Ponto Focal)',
    ver_como: 'Ver como padrinho (somente leitura)',
    cartas: 'Prazos das cartas',
    inscricoes: 'Inscrição de crianças',
    parceiros: 'Igrejas e organizações parceiras',
    embaixadores: 'Embaixadores',
    marketing: 'Marketing digital',
    indicadores: 'Indicadores da base',
    'equipe.ver': 'Ver a equipe',
    'equipe.gerenciar': 'Convidar, desligar e mudar acessos',
}

export const PERMISSION_GROUPS: { label: string; items: Permission[] }[] = [
    { label: 'Live Portal', items: ['fichas.ver', 'fichas.editar', 'export.basico', 'export.dados_pessoais'] },
    { label: 'Receita', items: ['recorrencias.ver', 'estorno.pedir', 'estorno.aprovar', 'estorno.aprovar_alto', 'retencao', 'conciliacao', 'conciliacao.revisar'] },
    { label: 'Portal do padrinho', items: ['moderacao', 'salvaguarda.relatar', 'salvaguarda.casos', 'ver_como', 'cartas'] },
    { label: 'Rede e gestão', items: ['inscricoes', 'parceiros', 'embaixadores', 'marketing', 'indicadores'] },
    { label: 'Administração', items: ['equipe.ver', 'equipe.gerenciar'] },
]

/** Every permission appears in exactly one group (checked by a test). */
export const groupedPermissions = () => PERMISSION_GROUPS.flatMap(g => g.items)

export type RoleColumn = { role: AdminRole; label: string }
export const ROLE_COLUMNS: RoleColumn[] = ADMIN_ROLES.map(role => ({ role, label: ROLE_LABELS[role] }))

export const roleHas = (role: AdminRole, permission: Permission) => can(role, permission)

export const allPermissions = () => [...PERMISSIONS]

export type PeopleFilter = { q: string; role: AdminRole | 'all'; department: string | 'all'; view: 'ativos' | 'terceiros' | 'desligados' | 'saiu_rh' }

export function filterPeople(people: Person[], f: PeopleFilter, leftWithAccess: string[]): Person[] {
    return people.filter(p => {
        if (f.view === 'desligados' ? p.status !== 'desligado' : p.status === 'desligado') return false
        if (f.view === 'terceiros' && p.kind !== 'terceiro') return false
        if (f.view === 'saiu_rh' && !leftWithAccess.includes(p.id)) return false
        if (f.role !== 'all' && p.role !== f.role) return false
        if (f.department !== 'all' && p.department !== f.department) return false
        // Accent- and case-insensitive; every word has to appear
        return matchesSearch(f.q, p.name, p.department, p.jobTitle, p.company)
    })
}

/** "3 h 59 min" until the end; null when already over. */
export function remaining(untilIso: string | null, nowMs: number): string | null {
    if (!untilIso) return null
    const left = Date.parse(untilIso) - nowMs
    if (left <= 0) return null
    const totalMin = Math.ceil(left / 60_000)
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    return h > 0 ? `${h} h ${m} min` : `${m} min`
}

/** Days between today and "2026-11-01" (negative = expired). */
export function daysUntil(day: string, nowMs: number): number {
    const today = Date.parse(new Date(nowMs).toISOString().slice(0, 10))
    return Math.round((Date.parse(day) - today) / 86_400_000)
}

export function validityLabel(p: Person, nowMs: number): { text: string; tone: 'ok' | 'warn' | 'err' } | null {
    if (p.kind !== 'terceiro' || !p.validUntil) return null
    const d = daysUntil(p.validUntil, nowMs)
    if (d < 0) return { text: 'expirado', tone: 'err' }
    if (d <= 15) return { text: d === 0 ? 'expira hoje' : `expira em ${d} ${d === 1 ? 'dia' : 'dias'}`, tone: 'warn' }
    return { text: `válido até ${p.validUntil.split('-').reverse().join('/')}`, tone: 'ok' }
}

export const reviewOverdue = (dueIso: string, nowMs: number) => Date.parse(dueIso) < nowMs

export const LOG_LABELS: Record<LogKind, string> = {
    acesso: 'Acessos e papéis', admin: 'Admin por tempo', export: 'Exportações', estorno: 'Estornos', descarte: 'Descarte de fotos',
    salvaguarda: 'Salvaguarda', retencao: 'Retenção', vercomo: 'Ver como',
}

export function filterLog(log: LogEntry[], kind: LogKind | 'all'): LogEntry[] {
    return kind === 'all' ? log : log.filter(e => e.kind === kind)
}

export const shortDate = (isoString: string) => {
    const d = new Date(isoString)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** The header sentence (calm style): who has access and whether anyone who left in HR still does. */
export function teamSentence(withAccess: number, leftWithAccess: number): string {
    const head = `${withAccess} ${withAccess === 1 ? 'pessoa tem' : 'pessoas têm'} acesso ao Staff`
    if (leftWithAccess === 0) return `${head}; ninguém que saiu no RH segue com acesso.`
    return `${head}; ${leftWithAccess} ${leftWithAccess === 1 ? 'saiu no RH e ainda entra' : 'saíram no RH e ainda entram'}.`
}
